import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Chip,
  Divider
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Description as PdfIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import eventService from '../../services/eventService';

// Validation schema
const schema = yup.object({
  eventName: yup.string().required('Event name is required').max(200),
  description: yup.string().required('Description is required').max(5000),
  category: yup.string().required('Category is required'),
  venue: yup.string().required('Venue is required'),
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  startDate: yup.string().required('Start date is required'),
  startTime: yup.string().required('Start time is required'),
  endDate: yup.string().nullable(),
  endTime: yup.string(),
  capacity: yup.number().min(0, 'Capacity must be positive').nullable(),
  rsvpDeadline: yup.string().nullable(),
  status: yup.string().oneOf(['draft', 'published', 'cancelled', 'completed']).required()
}).required();

const EditEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingEvent, setFetchingEvent] = useState(true);
  const [error, setError] = useState('');
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [existingBanner, setExistingBanner] = useState('');
  const [brochureFile, setBrochureFile] = useState(null);
  const [existingBrochure, setExistingBrochure] = useState(null);
  const [customFields, setCustomFields] = useState([]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      eventName: '',
      description: '',
      category: 'General',
      venue: '',
      address: '',
      city: '',
      state: '',
      country: 'USA',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      capacity: 0,
      rsvpEnabled: true,
      rsvpDeadline: '',
      requireApproval: false,
      status: 'draft',
      tags: []
    }
  });

  // Fetch event data
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setFetchingEvent(true);
        const response = await eventService.getEventById(id);

        if (response.success) {
          const event = response.data.event;

          // Debug logging
          console.log('Loaded event data:', {
            customFields: event.customFields,
            brochure: event.brochure
          });

          // Format dates for input fields
          const formatDateForInput = (dateString) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
          };

          // Populate form with existing data
          reset({
            eventName: event.eventName || '',
            description: event.description || '',
            category: event.category || 'General',
            venue: event.location?.venue || '',
            address: event.location?.address || '',
            city: event.location?.city || '',
            state: event.location?.state || '',
            country: event.location?.country || 'USA',
            startDate: event.dates?.[0]?.startDate ? formatDateForInput(event.dates[0].startDate) : '',
            endDate: event.dates?.[0]?.endDate ? formatDateForInput(event.dates[0].endDate) : '',
            startTime: event.dates?.[0]?.startTime || '',
            endTime: event.dates?.[0]?.endTime || '',
            capacity: event.capacity || 0,
            rsvpEnabled: event.rsvpEnabled !== undefined ? event.rsvpEnabled : true,
            rsvpDeadline: event.rsvpDeadline ? formatDateForInput(event.rsvpDeadline) : '',
            requireApproval: event.requireApproval || false,
            status: event.status || 'draft',
            tags: event.tags || []
          });

          // Set existing banner
          if (event.banner?.url) {
            setExistingBanner(eventService.getImageUrl(event.banner.url));
          }

          // Set existing brochure - always reset state
          setExistingBrochure(event.brochure || null);

          // Set custom fields - always reset state
          if (event.customFields && event.customFields.length > 0) {
            setCustomFields(event.customFields.map((field, idx) => ({
              ...field,
              id: Date.now() + idx, // Add temporary ID for React key
              options: field.options || []
            })));
          } else {
            setCustomFields([]);
          }
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch event');
      } finally {
        setFetchingEvent(false);
      }
    };

    fetchEvent();
  }, [id, reset]);

  const handleBannerChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setBannerFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBrochureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('PDF size must be less than 10MB');
        return;
      }

      // More flexible PDF detection
      const isPDF = file.type === 'application/pdf' ||
                    file.type === 'application/x-pdf' ||
                    file.type === 'application/octet-stream' ||
                    file.name.toLowerCase().endsWith('.pdf');

      if (!isPDF) {
        setError('Please select a PDF file');
        return;
      }

      setBrochureFile(file);
      setError('');
      console.log('Brochure file selected:', file.name, file.size, file.type);
    }
  };

  const addCustomField = () => {
    setCustomFields([...customFields, {
      id: Date.now(),
      fieldName: '',
      fieldLabel: '',
      fieldType: 'text',
      required: false,
      placeholder: '',
      options: [],
      order: customFields.length
    }]);
  };

  const removeCustomField = (id) => {
    setCustomFields(customFields.filter(field => field.id !== id));
  };

  const updateCustomField = (id, key, value) => {
    setCustomFields(customFields.map(field =>
      field.id === id ? { ...field, [key]: value } : field
    ));
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');

      // Prepare custom fields - remove temporary id and clean up
      const cleanedCustomFields = customFields.map(({ id, ...field }) => ({
        fieldName: (field.fieldLabel || '').trim().toLowerCase().replace(/\s+/g, '_'),
        fieldLabel: field.fieldLabel,
        fieldType: field.fieldType,
        required: field.required,
        placeholder: field.placeholder,
        ...(field.fieldType === 'select' && { options: field.options }),
        order: field.order
      })).filter(field => field.fieldName && field.fieldLabel);

      // Prepare event data
      const eventData = {
        ...data,
        location: {
          venue: data.venue,
          address: data.address,
          city: data.city,
          state: data.state,
          country: data.country || 'USA'
        },
        dates: [{
          startDate: data.startDate,
          endDate: data.endDate || data.startDate,
          startTime: data.startTime,
          endTime: data.endTime || data.startTime
        }],
        customFields: cleanedCustomFields
      };

      // Only include banner if a new file was selected
      if (bannerFile) {
        eventData.banner = bannerFile;
      }

      // Only include brochure if a new file was selected
      if (brochureFile) {
        eventData.brochure = brochureFile;
      }

      // Remove individual location fields
      delete eventData.venue;
      delete eventData.address;
      delete eventData.city;
      delete eventData.state;
      delete eventData.country;
      delete eventData.startDate;
      delete eventData.endDate;
      delete eventData.startTime;
      delete eventData.endTime;

      // Debug logging
      console.log('Submitting event update with data:', {
        ...eventData,
        banner: eventData.banner ? 'File object' : 'Not included',
        brochure: eventData.brochure ? 'File object' : 'Not included',
        brochureFile: brochureFile ? `File: ${brochureFile.name}` : 'No new brochure',
        customFields: eventData.customFields
      });

      const response = await eventService.updateEvent(id, eventData);

      console.log('Event update response:', response);

      if (response.success) {
        navigate(`/admin/events/${id}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update event');
      setLoading(false);
    }
  };

  if (fetchingEvent) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Edit Event
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Section 1: Basic Info */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
              Basic Information
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
              }}
            >
              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <Controller
                  name="eventName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Event Name"
                      error={!!errors.eventName}
                      helperText={errors.eventName?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={4}
                      label="Description"
                      error={!!errors.description}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Category"
                      error={!!errors.category}
                      helperText={errors.category?.message}
                    >
                      <MenuItem value="General">General</MenuItem>
                      <MenuItem value="Conference">Conference</MenuItem>
                      <MenuItem value="Workshop">Workshop</MenuItem>
                      <MenuItem value="Seminar">Seminar</MenuItem>
                      <MenuItem value="Meetup">Meetup</MenuItem>
                      <MenuItem value="Social">Social</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Section 2: Location */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
              Location Details
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
              }}
            >
              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <Controller
                  name="venue"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Venue Name"
                      error={!!errors.venue}
                      helperText={errors.venue?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Address"
                      error={!!errors.address}
                      helperText={errors.address?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="City"
                      error={!!errors.city}
                      helperText={errors.city?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="State"
                      error={!!errors.state}
                      helperText={errors.state?.message}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Section 3: Date & Time */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
              Date & Time
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
              }}
            >
              <Box>
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      label="Start Date"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      error={!!errors.startDate}
                      helperText={errors.startDate?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="startTime"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="time"
                      label="Start Time"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.startTime}
                      helperText={errors.startTime?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      label="End Date (Optional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.endDate}
                      helperText={errors.endDate?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="endTime"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="time"
                      label="End Time (Optional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.endTime}
                      helperText={errors.endTime?.message}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Section 4: Settings & Publish */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
              Settings & Publish
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 3,
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
              }}
            >
              <Box>
                <Controller
                  name="capacity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Capacity (0 for unlimited)"
                      error={!!errors.capacity}
                      helperText={errors.capacity?.message}
                    />
                  )}
                />
              </Box>
              <Box>
                <Controller
                  name="rsvpDeadline"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      label="RSVP Deadline (Optional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.rsvpDeadline}
                      helperText={errors.rsvpDeadline?.message}
                    />
                  )}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <Typography variant="subtitle1" gutterBottom>
                  Event Banner
                </Typography>
                {existingBanner && !bannerPreview && (
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Current Banner:
                    </Typography>
                    <img
                      src={existingBanner}
                      alt="Current banner"
                      style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                    />
                  </Box>
                )}
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="banner-upload"
                  type="file"
                  onChange={handleBannerChange}
                />
                <label htmlFor="banner-upload">
                  <Button variant="outlined" component="span">
                    {existingBanner ? 'Change Banner' : 'Upload Banner'}
                  </Button>
                </label>
                {bannerPreview && (
                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      New Banner Preview:
                    </Typography>
                    <img
                      src={bannerPreview}
                      alt="Banner preview"
                      style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                    />
                  </Box>
                )}
              </Box>
              <Box>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Status"
                      error={!!errors.status}
                      helperText={errors.status?.message}
                    >
                      <MenuItem value="draft">Draft</MenuItem>
                      <MenuItem value="published">Published</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </TextField>
                  )}
                />
              </Box>
            </Box>
          </Box>

          {/* Brochure Upload Section */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Event Brochure (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a PDF brochure that will be sent to attendees in confirmation emails (Max 10MB)
            </Typography>

            {existingBrochure && !brochureFile && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Current Brochure:
                </Typography>
                <Chip
                  icon={<PdfIcon />}
                  label={existingBrochure.originalName || 'event-brochure.pdf'}
                  sx={{ mt: 1 }}
                  onDelete={() => setExistingBrochure(null)}
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  ({existingBrochure?.size ? (existingBrochure.size / 1024 / 1024).toFixed(2) : '?'} MB)
                </Typography>
              </Box>
            )}

            <input
              accept=".pdf,application/pdf"
              style={{ display: 'none' }}
              id="brochure-upload"
              type="file"
              onChange={handleBrochureChange}
            />
            <label htmlFor="brochure-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PdfIcon />}
              >
                {existingBrochure || brochureFile ? 'Change Brochure' : 'Upload Brochure'}
              </Button>
            </label>

            {brochureFile && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  New Brochure:
                </Typography>
                <Chip
                  icon={<PdfIcon />}
                  label={brochureFile.name}
                  onDelete={() => setBrochureFile(null)}
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                  ({(brochureFile.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Custom RSVP Fields Builder */}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Custom RSVP Fields (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add custom fields that attendees must fill when registering
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={addCustomField}
                size="small"
              >
                Add Field
              </Button>
            </Box>

            {customFields.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {customFields.map((field, index) => (
                  <Card key={field.id} variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="subtitle2" color="primary">
                          Field {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeCustomField(field.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                          <TextField
                            fullWidth
                            label="Field Label"
                            placeholder="e.g., Clinic Name"
                            value={field.fieldLabel}
                            onChange={(e) => updateCustomField(field.id, 'fieldLabel', e.target.value)}
                            size="small"
                          />
                          <FormControl fullWidth size="small">
                            <InputLabel>Field Type</InputLabel>
                            <Select
                              value={field.fieldType}
                              label="Field Type"
                              onChange={(e) => updateCustomField(field.id, 'fieldType', e.target.value)}
                            >
                              <MenuItem value="text">Text</MenuItem>
                              <MenuItem value="email">Email</MenuItem>
                              <MenuItem value="phone">Phone</MenuItem>
                              <MenuItem value="number">Number</MenuItem>
                              <MenuItem value="textarea">Textarea</MenuItem>
                              <MenuItem value="select">Dropdown</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        <TextField
                          fullWidth
                          label="Placeholder Text (Optional)"
                          placeholder="e.g., Enter your clinic name"
                          value={field.placeholder}
                          onChange={(e) => updateCustomField(field.id, 'placeholder', e.target.value)}
                          size="small"
                        />

                        {field.fieldType === 'select' && (
                          <TextField
                            fullWidth
                            label="Options (comma-separated)"
                            placeholder="e.g., Option 1, Option 2, Option 3"
                            value={field.options.join(', ')}
                            onChange={(e) => updateCustomField(field.id, 'options', e.target.value.split(',').map(o => o.trim()))}
                            size="small"
                            helperText="Separate options with commas"
                          />
                        )}

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={field.required}
                              onChange={(e) => updateCustomField(field.id, 'required', e.target.checked)}
                            />
                          }
                          label="Required field"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {customFields.length === 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 3,
                  textAlign: 'center',
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No custom fields added yet. Click "Add Field" to create custom RSVP fields.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Form Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/admin/events/${id}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Update Event'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EditEvent;
